import React from 'react';
import { BarChart3, BookOpen, Lightbulb, Pencil, PlayCircle } from 'lucide-react';
import type { QuizProgressStats, StudentProgressStats, VideoProgressStats } from '../../types/statistics';

interface MyProgressStatsSectionProps {
  stats: StudentProgressStats | null;
  videoProgress: VideoProgressStats | null;
  quizProgress: QuizProgressStats | null;
  loading: boolean;
}

const CircularScore: React.FC<{ percentage: number; gradientId: string }> = ({
  percentage,
  gradientId,
}) => {
  const clamped = Math.max(0, Math.min(100, Math.round(percentage)));
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div className="relative h-12 w-12 shrink-0 sm:h-14 sm:w-14">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 44 44" aria-hidden>
        <circle cx="22" cy="22" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="3.5" />
        <circle
          cx="22"
          cy="22"
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#001848" />
            <stop offset="100%" stopColor="#C09048" />
          </linearGradient>
        </defs>
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-[#001848] sm:text-xs">
        {clamped}%
      </span>
    </div>
  );
};

const ProgressCategoryCard: React.FC<{
  title: string;
  totalLabel: string;
  solvedLabel: string;
  scoreLabel: string;
  total: number;
  solved: number;
  averageScore: number;
  gradientId: string;
  icon: React.ReactNode;
}> = ({
  title,
  totalLabel,
  solvedLabel,
  scoreLabel,
  total,
  solved,
  averageScore,
  gradientId,
  icon,
}) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#001848] text-white sm:h-11 sm:w-11">
          {icon}
        </div>
        <h3 className="text-base font-bold text-[#001848] sm:text-lg">{title}</h3>
      </div>

      <div className="flex items-center gap-3">
        <Lightbulb className="h-5 w-5 shrink-0 text-gold-500 sm:h-6 sm:w-6" strokeWidth={1.75} />
        <p className="text-sm font-medium text-slate-500 sm:text-base">
          {total} {totalLabel}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <BarChart3 className="h-5 w-5 shrink-0 text-[#001848] sm:h-6 sm:w-6" strokeWidth={1.75} />
        <div>
          <p className="text-sm font-semibold text-[#001848] sm:text-base">{solvedLabel}</p>
          <p className="text-xs text-slate-400 sm:text-sm">
            {solved}/{total}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <CircularScore percentage={averageScore} gradientId={gradientId} />
        <p className="text-sm font-semibold text-[#001848] sm:text-base">{scoreLabel}</p>
      </div>
    </div>
  </div>
);

const VideoProgressCard: React.FC<{ videoProgress: VideoProgressStats }> = ({
  videoProgress,
}) => {
  const pct = Math.round(videoProgress.progress);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#001848] text-white sm:h-11 sm:w-11">
            <PlayCircle className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />
          </div>
          <h3 className="text-base font-bold text-[#001848] sm:text-lg">الفيديوهات</h3>
        </div>

        <div className="flex items-center gap-3">
          <Lightbulb className="h-5 w-5 shrink-0 text-gold-500 sm:h-6 sm:w-6" strokeWidth={1.75} />
          <p className="text-sm font-medium text-slate-500 sm:text-base">
            {videoProgress.totalVideos} فيديو
          </p>
        </div>

        <div className="flex items-center gap-3">
          <BarChart3 className="h-5 w-5 shrink-0 text-[#001848] sm:h-6 sm:w-6" strokeWidth={1.75} />
          <div>
            <p className="text-sm font-semibold text-[#001848] sm:text-base">تمت المشاهدة</p>
            <p className="text-xs text-slate-400 sm:text-sm">
              {videoProgress.watchedVideos}/{videoProgress.totalVideos}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <CircularScore percentage={pct} gradientId="videoProgressRing" />
          <p className="text-sm font-semibold text-[#001848] sm:text-base">نسبة الإنجاز</p>
        </div>
      </div>
    </div>
  );
};

const MyProgressStatsSection: React.FC<MyProgressStatsSectionProps> = ({
  stats,
  videoProgress,
  quizProgress,
  loading,
}) => {
  if (loading) {
    return (
      <section className="mt-4 text-right sm:mt-5">
        <div className="mb-3 h-5 w-24 animate-pulse rounded-lg bg-slate-200" />
        <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-2xl border border-slate-200 bg-white" />
          ))}
        </div>
      </section>
    );
  }

  if (!stats && !videoProgress && !quizProgress) {
    return null;
  }

  const quizAverage =
    quizProgress && quizProgress.results.length > 0
      ? Math.round(
          quizProgress.results.reduce((sum, r) => sum + r.score, 0) / quizProgress.results.length
        )
      : stats
        ? Math.round(stats.averageQuizScore)
        : 0;

  const showLessons = Boolean(stats && stats.totalLessons > 0);
  const showQuizzes = Boolean(quizProgress && quizProgress.totalQuizzes > 0);
  const showVideos = Boolean(videoProgress && videoProgress.totalVideos > 0);

  if (!showLessons && !showQuizzes && !showVideos) {
    return null;
  }

  return (
    <section className="mt-4 text-right sm:mt-5">
      <h2 className="mb-3 text-base font-bold text-[#001848] sm:mb-4 sm:text-lg">تقدّمك</h2>

      <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 sm:gap-4">
        {showLessons && stats && (
          <ProgressCategoryCard
            title="الدروس"
            totalLabel="درس"
            solvedLabel="مكتمل"
            scoreLabel="نسبة الإنجاز"
            total={stats.totalLessons}
            solved={stats.completedLessons}
            averageScore={stats.completionPercentage}
            gradientId="lessonsProgressRing"
            icon={<BookOpen className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />}
          />
        )}

        {showQuizzes && quizProgress && (
          <ProgressCategoryCard
            title="الاختبارات"
            totalLabel="اختبار"
            solvedLabel="محلول"
            scoreLabel="متوسط الدرجات"
            total={quizProgress.totalQuizzes}
            solved={quizProgress.solvedQuizzes}
            averageScore={quizAverage}
            gradientId="quizzesProgressRing"
            icon={<Pencil className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2} />}
          />
        )}

        {showVideos && videoProgress && <VideoProgressCard videoProgress={videoProgress} />}
      </div>
    </section>
  );
};

export default MyProgressStatsSection;
